
import React, { useState, useCallback } from "react";
import { BarChart, BookOpen, MessageSquare } from "lucide-react";
import { InsightCard } from "@/types/insight";
import { Poll } from "@/types/poll";
import CardHeader from "./CardHeader";
import AgreementBar from "./AgreementBar";
import LinkedPollsSection from "./LinkedPollsSection";
import ShareButton from "@/components/social/ShareButton";
import InsightAuthAlerts from "./InsightAuthAlerts";
import InsightRelatedContent from "./InsightRelatedContent";
import { User } from "@supabase/supabase-js";
import { TypographyH1, TypographyP, TypographyH3 } from "@/components/ui/typography";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LikeButton from "@/components/social/LikeButton";
import TrendingBadge from "@/components/social/TrendingBadge";
import { useSocialMetrics } from "@/hooks/useSocialMetrics";
import { useInsightCardRealtime } from "@/hooks/useInsightCardRealtime";
import { useCentralizedVoting } from "@/hooks/voting/useCentralizedVoting";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader as UICardHeader, CardTitle } from "@/components/ui/card";
import CardCommentsSection from "@/components/social/CardCommentsSection";

interface InsightContentProps {
  card: InsightCard;
  user: User | null;
  canVote: () => boolean;
  polls: Poll[];
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isLoadingPolls: boolean;
  handleRefreshPolls: () => void;
  isConnected?: boolean;
  relatedContent: any;
  hasRelatedContent: boolean;
}

const InsightContent = ({
  card,
  user,
  canVote,
  polls,
  setPolls,
  isOpen,
  setIsOpen,
  isLoadingPolls,
  handleRefreshPolls,
  isConnected = false,
  relatedContent,
  hasRelatedContent
}: InsightContentProps) => {
  console.log("InsightContent rendering with card:", card.title, "and polls:", polls.length);
  console.log("Real-time connection status:", isConnected);
  
  const [activeTab, setActiveTab] = useState('learn-more');
  const [cardData, setCardData] = useState<InsightCard>(card);
  const { metrics } = useSocialMetrics(cardData.id);
  
  // Handle real-time updates to card data
  const handleCardUpdate = useCallback((updatedCard: InsightCard) => {
    console.log('InsightContent: Received updated card data:', updatedCard);
    setCardData(updatedCard);
  }, []);
  
  // Set up real-time listener for card updates
  useInsightCardRealtime(cardData.id, handleCardUpdate);
  
  // Always show polls section if there are linked polls, regardless of card.linked_poll
  const hasLinkedPolls = polls.length > 0;

  // Create sharing URL based on the insight slug
  const shareUrl = cardData.slug 
    ? `${window.location.origin}/insight/${cardData.slug}`
    : window.location.href;

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border p-6">
      <div className="flex justify-between items-start mb-4">
        <CardHeader 
          category={cardData.category}
          linkedPoll={cardData.linked_poll}
          totalPollsters={cardData.totalPollsters}
        />
        
        <div className="flex items-center gap-2">
          <TrendingBadge 
            score={metrics?.heatScore || 0}
          />
          <ShareButton 
            contentId={cardData.id}
            contentType="card"
            initialShares={metrics?.sharesCount || 0}
          />
        </div>
      </div>

      <div className="mb-6">
        <TypographyH1 className="mb-4">{cardData.title}</TypographyH1>
        <TypographyP className="text-muted-foreground leading-relaxed mb-6">
          {cardData.description}
        </TypographyP>
        
        <AgreementBar 
          percentage={cardData.agreePercentage}
        />
      </div>

      {/* Show linked polls if available with real-time connection indicator */}
      {hasLinkedPolls && (
        <div className="mb-4">
          {/* Real-time connection indicator */}
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
            <span className="text-xs text-muted-foreground">
              {isConnected ? 'Live updates active' : 'Connecting...'}
            </span>
          </div>
          
          <LinkedPollsSection
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            linkedPolls={polls}
            isLoading={isLoadingPolls}
            onRefresh={handleRefreshPolls}
            canVote={canVote()}
          />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="learn-more">Learn More</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
        </TabsList>
        
        <TabsContent value="learn-more" className="mt-4">
          <div className="space-y-4">
            <InsightAuthAlerts user={user} canVote={canVote} />
            
            <Card>
              <UICardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Educational Resources
                </CardTitle>
              </UICardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Explore educational content related to this insight to deepen your understanding of the topic.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => window.open('/civic-education', '_blank')}
                >
                  Visit Education Hub
                </Button>
                
                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Related Educational Content</h4>
                  <p className="text-sm text-muted-foreground">
                    Access our comprehensive civic education resources including podcasts, videos, and document summaries 
                    to gain deeper insights into this topic and related civic issues.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Related Content Section - only shown in Learn More tab */}
            {hasRelatedContent && (
              <div className="bg-card rounded-lg shadow-md border border-border p-6">
                <InsightRelatedContent
                  relatedContent={relatedContent}
                  hasRelatedContent={hasRelatedContent}
                />
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="comments" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              <LikeButton 
                cardId={cardData.id}
                initialLikes={metrics?.likesCount || 0}
                showCount={true}
                contentType="card"
              />
              <div className="text-sm text-muted-foreground">
                Join the discussion about this insight
              </div>
            </div>
            
            <CardCommentsSection cardId={cardData.id} initialLimit={15} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InsightContent;
